import { DataTable } from '@/components'
import { format as formatLocalized } from '@/utils/date-fns/format'
import { useQuery } from '@apollo/client'
import { createColumnHelper } from '@tanstack/react-table'
import { useTranslate } from '@ur-solutions/react-hooks'
import { parseISO } from 'date-fns'
import { useMemo } from 'react'
import { useDriverWorkFilter } from './driver-work-filter'
import { formatHours } from './hours'
import { DRIVER_WORK_DAYS_QUERY } from './queries'
import { WorkBars } from './work-bars'

interface DayRow {
  id: string
  date: Date
  span: string
  totalHours: number
  driveHours: number
  workHours: number
  availableHours: number
}

const clock = (value: string | null | undefined) =>
  value ? formatLocalized(parseISO(value), 'HH:mm') : ''

export const DriverWorkDays: React.FC<{ user: string; weekStart: string }> = ({
  user,
  weekStart,
}) => {
  const translations = useTranslate({
    dayByDay: 'driver-work.day-by-day',
    day: 'common.day',
    firstLast: 'driver-work.first-last',
    total: 'driver-work.total',
    driving: 'common.driving',
    otherWork: 'common.other-work',
    availability: 'common.availability',
    noData: 'driver-work.no-data',
  })
  const { filter } = useDriverWorkFilter()
  const {
    data: current,
    previousData,
    loading,
  } = useQuery(DRIVER_WORK_DAYS_QUERY, {
    variables: {
      user,
      weekStart,
      year: filter.year,
      ownedVehiclesOnly: filter.ownedVehiclesOnly,
    },
  })
  const data = current ?? previousData

  const rows = useMemo<DayRow[]>(
    () =>
      (data?.driverWorkDays ?? []).map(day => ({
        id: day.date,
        date: parseISO(day.date),
        span: day.firstStart
          ? `${clock(day.firstStart)} – ${clock(day.lastEnd)}`
          : '–',
        totalHours: day.totalHours,
        driveHours: day.driveHours,
        workHours: day.workHours,
        availableHours: day.availableHours,
      })),
    [data]
  )

  const { day, firstLast, total, driving, otherWork, availability } =
    translations

  const columns = useMemo(() => {
    const column = createColumnHelper<DayRow>()
    const hours = (value: number) => (
      <span className="tabular-nums">{value ? formatHours(value) : '–'}</span>
    )
    return [
      column.accessor('date', {
        enableSorting: true,
        header: day,
        cell: info => (
          <span className="font-medium capitalize">
            {formatLocalized(info.getValue(), 'EEEE d. MMM')}
          </span>
        ),
      }),
      column.accessor('span', {
        enableSorting: true,
        header: firstLast,
        cell: info => <span className="tabular-nums">{info.getValue()}</span>,
      }),
      column.accessor('totalHours', {
        enableSorting: true,
        header: total,
        meta: { end: true },
        cell: info => (
          <span className="font-semibold tabular-nums">
            {info.getValue() ? formatHours(info.getValue()) : '–'}
          </span>
        ),
      }),
      column.accessor('driveHours', {
        enableSorting: true,
        header: driving,
        meta: { end: true },
        cell: info => hours(info.getValue()),
      }),
      column.accessor('workHours', {
        enableSorting: true,
        header: otherWork,
        meta: { end: true },
        cell: info => hours(info.getValue()),
      }),
      column.accessor('availableHours', {
        enableSorting: true,
        header: availability,
        meta: { end: true },
        cell: info => hours(info.getValue()),
      }),
    ]
  }, [availability, day, driving, firstLast, otherWork, total])

  return (
    <div className="space-y-3 rounded-2xl bg-white px-5 py-4 shadow-card">
      <div className="text-xs font-semibold uppercase text-stone-500">
        {translations.dayByDay}
      </div>
      <WorkBars
        height={120}
        bars={rows.map(row => ({
          key: row.id,
          label: formatLocalized(row.date, 'EEE'),
          title: `${formatLocalized(row.date, 'EEEE d. MMM')}: ${formatHours(
            row.totalHours
          )}`,
          driveHours: row.driveHours,
          workHours: row.workHours,
          availableHours: row.availableHours,
        }))}
      />
      <div className="overflow-x-auto">
        <DataTable
          columns={columns}
          data={rows}
          loading={loading}
          noDataText={translations.noData}
        />
      </div>
    </div>
  )
}
